import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";
import { Dashboard6Styles, Field, PagePreloader, CardHead, PageHeader } from "../components/ModalUI";

const SESSION_KEY = "zangon_session";

function getSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

function setSession(data) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

export default function Profile() {
  const session = getSession() || {};
  const fileInputRef = useRef(null);
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    setTimeout(() => setPageReady(true), 300);
  }, []);

  useEffect(() => {
    const body = document.getElementById("kt_app_body") || document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
      window.KTSwapper?.init?.();
    };

    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  // Profile Info Form State
  const [firstName, setFirstName] = useState(session.firstName || "System");
  const [lastName, setLastName] = useState(session.lastName || "Admin");
  const [email, setEmail] = useState(session.email || "admin@zangon.com");
  const [phone, setPhone] = useState(session.phoneNumber || session.phone || "+234 800 000 0000");
  const [profileImage, setProfileImage] = useState(session.profileImage || "/assets/media/zangon/pkaceholder.jpg");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password Form State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Image Upload Handler
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file size must be under 5MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setProfileImage(event.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  // Save Profile Handler
  const handleSaveProfile = (e) => {
    e.preventDefault();
    if (!firstName.trim()) {
      toast.error("First name is required.");
      return;
    }

    setIsUpdatingProfile(true);

    setTimeout(() => {
      const updatedSession = {
        ...session,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumber: phone.trim(),
        profileImage: profileImage,
      };

      setSession(updatedSession);
      setIsUpdatingProfile(false);
      toast.success("Profile details updated successfully!");

      // Dispatch event to notify other components (e.g. Sidebar)
      window.dispatchEvent(new Event("storage"));
    }, 600);
  };

  // Update Password Handler
  const handleUpdatePassword = (e) => {
    e.preventDefault();

    if (!currentPassword) {
      toast.error("Please enter your current password.");
      return;
    }
    if (!newPassword) {
      toast.error("Please enter a new password.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }

    setIsUpdatingPassword(true);

    setTimeout(() => {
      setIsUpdatingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully!");
    }, 800);
  };

  const pwField = (label, value, setValue, show, setShow, placeholder) => (
    <Field label={label} required>
      <div className="position-relative">
        <input
          type={show ? "text" : "password"}
          className="d6m-input"
          style={{ paddingRight: "2.75rem" }}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          required
        />
        <button type="button" className="btn btn-icon btn-sm position-absolute end-0 top-50 translate-middle-y me-2" onClick={() => setShow(!show)}>
          <i className={`ki-outline ${show ? "ki-eye-slash" : "ki-eye"} fs-5 text-gray-600`} />
        </button>
      </div>
    </Field>
  );

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard">
                  <img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" />
                </Link>
              </div>
              <PageHeader breadcrumb="Admin Profile" title="Admin Profile Settings" />
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />

            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    <div className="d6-kpi-brand p-6 mb-5">
                      <div className="d-flex flex-column flex-md-row align-items-center gap-5 position-relative" style={{ zIndex: 2 }}>
                        <div className="position-relative flex-shrink-0">
                          <span className="d6m-avatar" style={{ width: 92, height: 92, border: "3px solid rgba(255,255,255,0.85)" }}>
                            <img
                              src={profileImage}
                              alt="Admin Avatar"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/assets/media/zangon/pkaceholder.jpg";
                              }}
                            />
                          </span>
                          <button
                            type="button"
                            className="d6-icon-btn position-absolute bottom-0 end-0"
                            style={{ background: "#fff" }}
                            onClick={() => fileInputRef.current?.click()}
                            title="Change photo"
                          >
                            <i className="ki-outline ki-pencil text-success fs-6" />
                          </button>
                        </div>

                        <div className="text-center text-md-start text-white flex-grow-1 min-w-0">
                          <h2 className="d6-hero-title text-white mb-1">{firstName} {lastName}</h2>
                          <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-3 fs-8 text-white text-opacity-80">
                            <span><i className="ki-outline ki-sms text-white fs-6 me-1" />{email}</span>
                            <span>•</span>
                            <span><i className="ki-outline ki-shield-tick text-white fs-6 me-1" />System Administrator</span>
                          </div>
                        </div>

                        <button type="button" className="btn btn-light d6-mbtn" onClick={() => fileInputRef.current?.click()}>
                          <i className="ki-outline ki-file-up fs-5 me-2.5 text-success" />
                          Upload Photo
                        </button>
                      </div>
                    </div>

                    <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="d-none" />

                    <div className="row g-5 mb-5">
                      <div className="col-12 col-xl-7">
                        <div className="d6-card p-6 h-100">
                          <CardHead title="Personal Details" subtitle="Update your account identity and contact information" className="mb-5" />
                          <form onSubmit={handleSaveProfile} className="d-flex flex-column gap-4">
                            <div className="row g-4">
                              <Field className="col-6" label="First name" required>
                                <input type="text" className="d6m-input" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                              </Field>
                              <Field className="col-6" label="Last name">
                                <input type="text" className="d6m-input" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                              </Field>
                            </div>
                            <Field label="Email address">
                              <input type="email" className="d6m-input" placeholder="admin@zangon.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Field>
                            <Field label="Phone number">
                              <input type="text" className="d6m-input" placeholder="+234 800 000 0000" value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </Field>
                            <Field label="Profile image URL" hint="You can upload a photo using the button above or paste an image URL here.">
                              <input
                                type="text"
                                className="d6m-input"
                                placeholder="https://..."
                                value={profileImage.startsWith("data:") ? "(Uploaded local file)" : profileImage}
                                onChange={(e) => {
                                  if (!e.target.value.startsWith("(Uploaded")) setProfileImage(e.target.value);
                                }}
                              />
                            </Field>
                            <div className="d-flex justify-content-end pt-4 border-top border-gray-200">
                              <button type="submit" className="btn btn-success d6-mbtn" disabled={isUpdatingProfile}>
                                {isUpdatingProfile ? (
                                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</>
                                ) : (
                                  "Save Profile"
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>

                      <div className="col-12 col-xl-5">
                        <div className="d6-card p-6 h-100">
                          <CardHead title="Security & Password" subtitle="Manage your account authentication credentials" className="mb-5" />
                          <form onSubmit={handleUpdatePassword} className="d-flex flex-column gap-4">
                            {pwField("Current password", currentPassword, setCurrentPassword, showCurrentPw, setShowCurrentPw, "••••••••")}
                            {pwField("New password", newPassword, setNewPassword, showNewPw, setShowNewPw, "Minimum 6 characters")}
                            {pwField("Confirm new password", confirmPassword, setConfirmPassword, showConfirmPw, setShowConfirmPw, "Re-enter new password")}
                            <div className="d-flex justify-content-end pt-4 border-top border-gray-200">
                              <button type="submit" className="btn btn-success d6-mbtn" disabled={isUpdatingPassword}>
                                {isUpdatingPassword ? (
                                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Updating…</>
                                ) : (
                                  "Update Password"
                                )}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <div id="kt_app_footer" className="app-footer">
                  <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                    <div className="text-gray-900 text-center">
                      <span className="text-muted fw-semibold me-1">2026&copy;</span>
                      <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success d-flex align-items-center justify-content-center" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up fs-2 text-white" />
          </div>
        </div>
      </div>
    </>
  );
}
